import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { createExpense, getExpenses, editExpense, getExpenseCategories, createExpenseCategory, deleteExpenseCategory, getSuppliers, getStores, deleteExpense } from '../api/api';
import { Plus, Edit2, AlertCircle, Tag, Trash2, Globe, Building2 } from 'lucide-react';
import PrivacyBlur, { PrivacyNoticeBanner } from '../components/PrivacyBlur';
import { shouldBlurData, isCashierRole } from '../utils/privacyUtils';
import IntegrationNotice from '../components/IntegrationNotice';

const Expenses = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState({
    expense_type: '',
    description: '',
    amount: '',
    cashier_name: '',
    supplier_id: '',
    // Campos para depósitos
    deposit_bank: '',
    deposit_receipt_number: '',
    // NUEVO: Método de pago del gasto
    payment_method: 'efectivo',  // "efectivo" o "transferencia"
    // Campos para pagos por transferencia
    is_transfer_payment: false,
    transfer_reference: '',
    // Para admin seleccionar tienda
    store_id: ''
  });
  const [editData, setEditData] = useState({
    amount: '',
    edit_reason: '',
    authorization_code: ''
  });
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryIsGlobal, setNewCategoryIsGlobal] = useState(true);
  const [newCategoryStoreId, setNewCategoryStoreId] = useState('');
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    expense_type: '',
    store_id: '',
    category: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Lista de bancos disponibles
  const bancos = ['Banrural', 'BAC', 'Industrial', 'G&T Continental', 'Banco de los Trabajadores', 'Banco Promerica', 'Otro'];

  const isAdminOrSupervisor = ['admin', 'manager', 'supervisor', 'admin_assistant'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('¿Estás seguro de eliminar este gasto? Esta acción no se puede deshacer.')) return;
    
    try {
      await deleteExpense(expenseId);
      setSuccess('Gasto eliminado exitosamente');
      loadExpenses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar el gasto');
      setTimeout(() => setError(''), 3000);
    }
  };

  const loadStores = useCallback(async () => {
    try {
      const data = await getStores();
      setStores(data);
      if (data.length > 0) {
        setNewCategoryStoreId(data[0].id);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  }, []);

  const loadSuppliers = useCallback(async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await getExpenseCategories();
      setCategories(data);
      if (data.length > 0) {
        setFormData(prev => prev.expense_type ? prev : { ...prev, expense_type: data[0].name });
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) {
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59);
        params.end_date = endDate.toISOString();
      }
      if (filters.expense_type) params.expense_type = filters.expense_type;
      if (filters.store_id) params.store_id = filters.store_id;
      
      const data = await getExpenses(params);
      setExpenses(data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCategories();
    loadSuppliers();
    if (isAdminOrSupervisor) {
      loadStores();
    }
  }, [isAdminOrSupervisor, loadCategories, loadSuppliers, loadStores]);

  useEffect(() => {
    if (categories.length > 0) {
      loadExpenses();
    }
  }, [categories, loadExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validar que admin seleccione tienda
    if (isAdmin && !formData.store_id) {
      setError('Debe seleccionar una tienda');
      return;
    }
    
    setSubmitting(true);

    try {
      // Determinar si es transferencia basándose en el método de pago seleccionado
      const esTransferencia = formData.payment_method === 'transferencia';
      
      const expenseData = {
        expense_type: formData.expense_type,
        description: formData.description,
        amount: parseFloat(formData.amount),
        cashier_name: formData.cashier_name || user?.name || 'N/A',
        // NUEVO: Enviar método de pago
        payment_method: formData.payment_method,
        // También enviar is_transfer_payment para compatibilidad
        is_transfer_payment: esTransferencia
      };
      
      // Si admin seleccionó tienda, incluirla
      if (isAdmin && formData.store_id) {
        expenseData.store_id = formData.store_id;
      }
      
      // Si es gasto de proveedor, incluir el supplier_id
      if (formData.supplier_id) {
        expenseData.supplier_id = formData.supplier_id;
      }
      
      // Si es pago por transferencia, incluir referencia
      if (esTransferencia && formData.transfer_reference) {
        expenseData.transfer_reference = formData.transfer_reference;
      }
      
      // Si es depósito, incluir banco y número de boleta
      if (formData.expense_type.toLowerCase().includes('depósito') || formData.expense_type.toLowerCase().includes('deposito')) {
        if (formData.deposit_bank) {
          expenseData.deposit_bank = formData.deposit_bank;
        }
        if (formData.deposit_receipt_number) {
          expenseData.deposit_receipt_number = formData.deposit_receipt_number;
        }
      }
      
      console.log('Enviando gasto:', expenseData);  // Debug
      
      await createExpense(expenseData);
      
      const transferMsg = esTransferencia ? ' (TRANSFERENCIA - No afectó el flujo de caja)' : '';
      setSuccess('¡Gasto registrado exitosamente!' + transferMsg);
      setFormData({ 
        expense_type: categories[0]?.name || '', 
        description: '', 
        amount: '', 
        cashier_name: '', 
        supplier_id: '',
        deposit_bank: '',
        deposit_receipt_number: '',
        payment_method: 'efectivo',
        is_transfer_payment: false,
        transfer_reference: '',
        store_id: ''
      });
      setShowForm(false);
      loadExpenses();
    } catch (error) {
      console.error('Error creating expense:', error);
      // Handle Pydantic validation errors (array format)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMessages = detail.map(err => `${err.loc?.join('.') || 'Campo'}: ${err.msg}`).join(', ');
        setError(errorMessages);
      } else {
        setError(detail || 'Error al registrar el gasto');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setEditData({
      amount: expense.amount.toString(),
      edit_reason: '',
      authorization_code: ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await editExpense(selectedExpense.id, {
        ...editData,
        amount: parseFloat(editData.amount)
      });
      setSuccess('¡Gasto editado exitosamente!');
      setShowEditModal(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al editar el gasto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      const categoryData = {
        name: newCategory.trim(),
        is_global: newCategoryIsGlobal,
        store_id: newCategoryIsGlobal ? null : newCategoryStoreId
      };
      await createExpenseCategory(categoryData);
      setSuccess('¡Categoría creada exitosamente!');
      setNewCategory('');
      setNewCategoryIsGlobal(true);
      loadCategories();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear la categoría');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('¿Está seguro de eliminar esta categoría?')) return;

    try {
      await deleteExpenseCategory(categoryId);
      setSuccess('¡Categoría eliminada exitosamente!');
      loadCategories();
      loadExpenses();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar la categoría');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Asegurar que la fecha se interprete como UTC
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      utcDateString = dateString + 'Z';
    }
    const date = new Date(utcDateString);
    return date.toLocaleString('es-GT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isReadOnly = user?.role === 'admin_assistant';
  const canEdit = !isReadOnly && (user?.role === 'store_manager' || user?.role === 'admin');
  const canManageCategories = !isReadOnly && ['admin', 'manager', 'supervisor', 'store_manager'].includes(user?.role);
  const canCreate = !isReadOnly;

  return (
    <div className="space-y-8">
      {/* Header futurista - Responsivo */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent" data-testid="expenses-title">
            Registro de Gastos
          </h1>
          <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Controla todos los gastos de tu tienda</p>
        </div>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-3">
          {canManageCategories && (
            <button
              onClick={() => setShowCategoriesModal(true)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 font-semibold text-sm sm:text-base"
            >
              <Tag size={20} />
              <span>Categorías</span>
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 font-semibold text-sm sm:text-base"
              data-testid="add-expense-button"
            >
              <Plus size={20} />
              <span>Nuevo Gasto</span>
            </button>
          )}
        </div>
      </div>

      {/* Aviso de integración Portal de Pagos */}
      <IntegrationNotice />

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center space-x-3" data-testid="expenses-error">
          <span className="text-2xl">⚠️</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center space-x-3 animate-pulse" data-testid="expenses-success">
          <span className="text-2xl">✅</span>
          <span className="font-medium">{success}</span>
        </div>
      )}

      {/* Filtros con diseño moderno - Responsivo */}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 border border-gray-100">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <Tag className="text-red-600" size={20} />
          <span>Filtros de Búsqueda</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {/* Filtro por Tienda - Solo para Admin/Manager/Supervisor */}
          {isAdminOrSupervisor && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tienda
              </label>
              <select
                value={filters.store_id}
                onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              >
                <option value="">Todas las tiendas</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoría
            </label>
            <select
              value={filters.expense_type}
              onChange={(e) => setFilters({ ...filters, expense_type: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            >
              <option value="">Todas las categorías</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ start_date: '', end_date: '', expense_type: '', store_id: '' })}
              className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm sm:text-base"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Registrar Nuevo Gasto</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Selector de tienda para admin */}
            {isAdmin && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  <Building2 className="inline mr-2" size={16} />
                  Seleccionar Tienda (Admin)
                </label>
                <select
                  value={formData.store_id}
                  onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                  className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                  required
                >
                  <option value="">-- Seleccione una tienda --</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría
                </label>
                <select
                  value={formData.expense_type}
                  onChange={(e) => setFormData({ ...formData, expense_type: e.target.value, supplier_id: '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="expense-type-select"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Descripción del gasto"
                  required
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto (Q)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                  data-testid="amount-input"
                />
              </div>
            </div>

            {/* Nombre del Cajero/Responsable */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                👤 Nombre del Cajero/Responsable
              </label>
              <input
                type="text"
                value={formData.cashier_name}
                onChange={(e) => setFormData({ ...formData, cashier_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nombre de quien realiza el gasto"
                required
                data-testid="cashier-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">Este campo identifica quién está realizando el gasto</p>
            </div>

            {/* Selector de Proveedor - solo si es categoría "Proveedores" */}
            {formData.expense_type === 'Proveedores' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar Proveedor
                </label>
                <select
                  value={formData.supplier_id}
                  onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="supplier-select"
                >
                  <option value="">Seleccionar proveedor...</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} - <span className="notranslate">{supplier.nit}</span>
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Campos para Depósito - solo si la categoría contiene "depósito" */}
            {(formData.expense_type.toLowerCase().includes('depósito') || formData.expense_type.toLowerCase().includes('deposito')) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  🏦 Información del Depósito
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Banco
                    </label>
                    <select
                      value={formData.deposit_bank}
                      onChange={(e) => setFormData({ ...formData, deposit_bank: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Seleccionar banco...</option>
                      {bancos.map((banco) => (
                        <option key={banco} value={banco}>{banco}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Boleta
                    </label>
                    <input
                      type="text"
                      value={formData.deposit_receipt_number}
                      onChange={(e) => setFormData({ ...formData, deposit_receipt_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ej: 123456789"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* NUEVO: Selector de Método de Pago */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-5 space-y-4">
              <h4 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                💰 Método de Pago del Gasto
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Opción Efectivo */}
                <label 
                  className={`relative flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.payment_method === 'efectivo' 
                      ? 'border-green-500 bg-green-50 shadow-lg' 
                      : 'border-gray-300 bg-white hover:border-green-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value="efectivo"
                    checked={formData.payment_method === 'efectivo'}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value, is_transfer_payment: false })}
                    className="absolute opacity-0"
                  />
                  <span className="text-3xl mb-2">💵</span>
                  <span className="font-bold text-green-700">EFECTIVO</span>
                  <span className="text-xs text-gray-600 text-center mt-1">
                    Se descuenta del flujo de caja
                  </span>
                  {formData.payment_method === 'efectivo' && (
                    <span className="absolute top-2 right-2 text-green-500 text-xl">✓</span>
                  )}
                </label>

                {/* Opción Transferencia */}
                <label 
                  className={`relative flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.payment_method === 'transferencia' 
                      ? 'border-purple-500 bg-purple-50 shadow-lg' 
                      : 'border-gray-300 bg-white hover:border-purple-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value="transferencia"
                    checked={formData.payment_method === 'transferencia'}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value, is_transfer_payment: true })}
                    className="absolute opacity-0"
                  />
                  <span className="text-3xl mb-2">🏦</span>
                  <span className="font-bold text-purple-700">TRANSFERENCIA</span>
                  <span className="text-xs text-gray-600 text-center mt-1">
                    NO afecta flujo de caja
                  </span>
                  {formData.payment_method === 'transferencia' && (
                    <span className="absolute top-2 right-2 text-purple-500 text-xl">✓</span>
                  )}
                </label>
              </div>

              {/* Mensaje informativo */}
              {formData.payment_method === 'efectivo' && (
                <div className="bg-green-100 border border-green-300 rounded-lg p-3 text-sm text-green-800">
                  <strong>💵 Pago en Efectivo:</strong> Este gasto SE DESCONTARÁ del flujo de caja de la tienda.
                </div>
              )}
              
              {formData.payment_method === 'transferencia' && (
                <>
                  <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm text-purple-800">
                    <strong>🏦 Pago por Transferencia:</strong> Este gasto NO se descontará del flujo de caja. 
                    El pago fue realizado desde oficinas centrales o cuenta bancaria.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Referencia de Transferencia (opcional)
                    </label>
                    <input
                      type="text"
                      value={formData.transfer_reference}
                      onChange={(e) => setFormData({ ...formData, transfer_reference: e.target.value })}
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Número de referencia, confirmación o boleta"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400"
                data-testid="submit-expense-button"
              >
                {submitting ? 'Registrando...' : 'Registrar Gasto'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Privacy Notice Banner for Cashiers */}
      <PrivacyNoticeBanner show={isCashierRole(user)} />

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Historial de Gastos</h2>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-12 h-12 border-4 border-red-500/30 border-t-red-600 rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Cargando gastos...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="p-12 text-center">
            <div className="bg-gray-100 rounded-full w-24 h-24 mx-auto flex items-center justify-center mb-4">
              <AlertCircle className="text-gray-400" size={48} />
            </div>
            <p className="text-gray-600 font-medium">No hay gastos registrados</p>
          </div>
        ) : (
          <>
            {/* Summary by category - Responsivo */}
            <div className="p-3 sm:p-4 bg-slate-50 border-b">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
                <span className="text-sm font-semibold text-slate-700">Por categoría:</span>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const byCategory = expenses.reduce((acc, exp) => {
                      const cat = exp.expense_type || 'Sin categoría';
                      acc[cat] = (acc[cat] || 0) + exp.amount;
                      return acc;
                    }, {});
                    return Object.entries(byCategory).map(([cat, total]) => (
                      <span 
                        key={cat} 
                        className={`px-2 py-1 text-xs rounded-lg cursor-pointer transition-all ${
                          filters.expense_type === cat 
                            ? 'bg-red-600 text-white' 
                            : 'bg-white border text-slate-600 hover:bg-red-50'
                        }`}
                        onClick={() => setFilters({ ...filters, expense_type: filters.expense_type === cat ? '' : cat })}
                      >
                        {cat}: Q{total.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                      </span>
                    ));
                  })()}
                </div>
                <span className="sm:ml-auto px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-lg">
                  Total: Q{expenses.reduce((sum, e) => sum + e.amount, 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            
            {/* Vista de Tabla para Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-700 to-red-700 text-white">
                    <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide rounded-tl-xl">Fecha/Hora</th>
                    {isAdminOrSupervisor && <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Tienda</th>}
                    <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Categoría</th>
                    <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Descripción</th>
                    <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Cajero</th>
                    <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Monto</th>
                    <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Registrado Por</th>
                    {(canEdit || isAdmin) && <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide rounded-tr-xl">Acciones</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {expenses.filter(e => !filters.expense_type || e.expense_type === filters.expense_type).map((expense, index) => {
                    const isBlurred = shouldBlurData(user, expense.timestamp);
                    return (
                    <tr key={expense.id} className={`hover:bg-red-50/50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${isBlurred ? 'opacity-75' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        <PrivacyBlur isBlurred={isBlurred}>
                          {formatDate(expense.timestamp)}
                        </PrivacyBlur>
                      </td>
                      {isAdminOrSupervisor && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-100 text-blue-800">
                            {expense.store_name || 'N/A'}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PrivacyBlur isBlurred={isBlurred}>
                          <span className="px-3 py-1.5 inline-flex text-xs font-bold rounded-lg shadow-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                            {expense.expense_type}
                          </span>
                        </PrivacyBlur>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium max-w-xs">
                        <PrivacyBlur isBlurred={isBlurred}>
                          <div className="truncate" title={expense.description}>{expense.description}</div>
                          {expense.is_transfer_payment && (
                            <div className="flex items-center space-x-1 text-xs text-purple-600 mt-1 bg-purple-50 rounded px-2 py-1 w-fit">
                              <span>💳 Transferencia</span>
                              {expense.transfer_reference && <span>- Ref: {expense.transfer_reference}</span>}
                            </div>
                          )}
                          {expense.deposit_bank && (
                            <div className="flex items-center space-x-1 text-xs text-blue-600 mt-1 bg-blue-50 rounded px-2 py-1 w-fit">
                              <span>🏦 {expense.deposit_bank}</span>
                              {expense.deposit_receipt_number && <span>- Boleta: {expense.deposit_receipt_number}</span>}
                            </div>
                          )}
                          {expense.is_petty_cash_refund && (
                            <div className="flex items-center space-x-1 text-xs text-orange-600 mt-1 bg-orange-50 rounded px-2 py-1 w-fit">
                              <span>💰 Reposición de Sencillo</span>
                            </div>
                          )}
                          {expense.edited_by && (
                            <div className="flex items-center space-x-1 text-xs text-amber-600 mt-1 bg-amber-50 rounded px-2 py-1 w-fit">
                              <Edit2 size={12} />
                              <span>Editado: {expense.edit_reason}</span>
                            </div>
                          )}
                        </PrivacyBlur>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        <PrivacyBlur isBlurred={isBlurred}>
                          {expense.cashier_name || 'N/A'}
                        </PrivacyBlur>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-base font-bold text-red-600">
                        <PrivacyBlur isBlurred={isBlurred}>
                          {expense.currency || 'Q'}{expense.amount.toFixed(2)}
                        </PrivacyBlur>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <PrivacyBlur isBlurred={isBlurred}>
                          {expense.created_by_name}
                        </PrivacyBlur>
                      </td>
                      {(canEdit || isAdmin) && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center space-x-2">
                            {canEdit && !isBlurred && (
                              <button
                                onClick={() => handleEdit(expense)}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center space-x-1 font-semibold text-xs"
                                data-testid="edit-expense-button"
                              >
                                <Edit2 size={14} />
                                <span>Editar</span>
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteExpense(expense.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Eliminar gasto"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Vista de Tarjetas para Móvil */}
            <div className="block md:hidden space-y-3 p-4">
              {expenses.filter(e => !filters.expense_type || e.expense_type === filters.expense_type).map((expense) => {
                const isBlurred = shouldBlurData(user, expense.timestamp);
                return (
                  <div 
                    key={expense.id} 
                    className={`bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow ${isBlurred ? 'opacity-75' : ''}`}
                    data-testid={`expense-card-${expense.id}`}
                  >
                    {/* Header: Monto y Categoría */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <PrivacyBlur isBlurred={isBlurred}>
                          <span className="px-3 py-1.5 inline-flex text-xs font-bold rounded-lg shadow-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                            {expense.expense_type}
                          </span>
                        </PrivacyBlur>
                      </div>
                      <PrivacyBlur isBlurred={isBlurred}>
                        <span className="text-xl font-bold text-red-600">
                          {expense.currency || 'Q'}{expense.amount.toFixed(2)}
                        </span>
                      </PrivacyBlur>
                    </div>

                    {/* Descripción */}
                    <div className="mb-3">
                      <PrivacyBlur isBlurred={isBlurred}>
                        <p className="text-sm text-gray-900 font-medium">{expense.description}</p>
                      </PrivacyBlur>
                      
                      {/* Badges de información adicional */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {expense.is_transfer_payment && (
                          <span className="px-2 py-1 text-xs text-purple-600 bg-purple-50 rounded">
                            💳 Transferencia {expense.transfer_reference && `- ${expense.transfer_reference}`}
                          </span>
                        )}
                        {expense.deposit_bank && (
                          <span className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded">
                            🏦 {expense.deposit_bank} {expense.deposit_receipt_number && `- ${expense.deposit_receipt_number}`}
                          </span>
                        )}
                        {expense.is_petty_cash_refund && (
                          <span className="px-2 py-1 text-xs text-orange-600 bg-orange-50 rounded">
                            💰 Reposición Sencillo
                          </span>
                        )}
                        {expense.edited_by && (
                          <span className="px-2 py-1 text-xs text-amber-600 bg-amber-50 rounded flex items-center gap-1">
                            <Edit2 size={10} /> Editado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Información Principal */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Cajero:</span>
                        <PrivacyBlur isBlurred={isBlurred}>
                          <span className="font-semibold text-gray-900">{expense.cashier_name || 'N/A'}</span>
                        </PrivacyBlur>
                      </div>
                      
                      {isAdminOrSupervisor && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Tienda:</span>
                          <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-100 text-blue-800">
                            {expense.store_name || 'N/A'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Registrado por:</span>
                        <PrivacyBlur isBlurred={isBlurred}>
                          <span className="text-gray-600">{expense.created_by_name}</span>
                        </PrivacyBlur>
                      </div>
                    </div>

                    {/* Footer: Fecha y Acciones */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <PrivacyBlur isBlurred={isBlurred}>
                        <span className="text-xs text-gray-500">
                          {formatDate(expense.timestamp)}
                        </span>
                      </PrivacyBlur>
                      {(canEdit || isAdmin) && (
                        <div className="flex items-center space-x-2">
                          {canEdit && !isBlurred && (
                            <button
                              onClick={() => handleEdit(expense)}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                              title="Editar gasto"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Eliminar gasto"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Editar Gasto</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nuevo Monto (Q)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.amount}
                  onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="edit-amount-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Razón de la Edición
                </label>
                <textarea
                  value={editData.edit_reason}
                  onChange={(e) => setEditData({ ...editData, edit_reason: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  required
                  data-testid="edit-reason-input"
                  placeholder="Explique por qué está editando este gasto"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código de Autorización
                </label>
                <input
                  type="text"
                  value={editData.authorization_code}
                  onChange={(e) => setEditData({ ...editData, authorization_code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="Ingrese el código"
                  data-testid="auth-code-input"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? 'Editando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedExpense(null);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories Modal */}
      {showCategoriesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Gestionar Categorías de Gastos</h2>
            
            <form onSubmit={handleAddCategory} className="mb-6 space-y-4">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nueva categoría..."
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Agregar
                </button>
              </div>
              
              {isAdminOrSupervisor && (
                <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="categoryScope"
                        checked={newCategoryIsGlobal}
                        onChange={() => setNewCategoryIsGlobal(true)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <Globe size={18} className="text-blue-600" />
                      <span className="font-medium">Global</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="categoryScope"
                        checked={!newCategoryIsGlobal}
                        onChange={() => setNewCategoryIsGlobal(false)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <Building2 size={18} className="text-green-600" />
                      <span className="font-medium">Tienda específica</span>
                    </label>
                  </div>
                  
                  {!newCategoryIsGlobal && (
                    <select
                      value={newCategoryStoreId}
                      onChange={(e) => setNewCategoryStoreId(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </form>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Categorías Actuales:</h3>
              {categories.map((category) => (
                <div key={category.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="font-medium">{category.name}</span>
                    {category.is_global ? (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full flex items-center space-x-1">
                        <Globe size={12} />
                        <span>Global</span>
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full flex items-center space-x-1">
                        <Building2 size={12} />
                        <span>{category.store_name || 'Tienda'}</span>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowCategoriesModal(false)}
                className="w-full px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
